import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface SpeedImprovementChartProps {
  data: Array<{
    quiz_date: string;
    avg_time_per_question: string;
  }> | null;
}

const SpeedImprovementChart: React.FC<SpeedImprovementChartProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-muted p-4">
        <i className="fas fa-tachometer-alt fa-2x mb-2"></i>
        <p>No speed data available</p>
      </div>
    );
  }

  // Sort data by date
  const sortedData = [...data].sort((a, b) => new Date(a.quiz_date).getTime() - new Date(b.quiz_date).getTime());

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const chartData = {
    labels: sortedData.map(item => formatDate(item.quiz_date)),
    datasets: [
      {
        label: 'Avg Time per Question (seconds)',
        data: sortedData.map(item => parseFloat(item.avg_time_per_question)),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: 'rgb(255, 99, 132)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#333',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgb(255, 99, 132)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function(context: any) {
            const dataIndex = context[0].dataIndex;
            return `Date: ${sortedData[dataIndex].quiz_date}`;
          },
          label: function(context: any) {
            return `Avg Time: ${parseFloat(context.parsed.y).toFixed(1)}s per question`;
          },
        },
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Quiz Date',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#333',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#666',
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Time per Question (seconds)',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#333',
        },
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#666',
          callback: function(value: any) {
            return value + 's';
          },
        },
      },
    },
  };

  return (
    <div style={{ height: '300px', width: '100%' }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default SpeedImprovementChart;
